import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, AlertCircle, CheckCircle } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useEffect } from "react";

interface PaymentSummaryProps {
  price?: number;
  currency?: string;
  discount: number;
  discountCode: string;
  onDiscountCodeChange: (code: string) => void;
  onApplyDiscount: () => void;
  onPaymentSubmit: () => void;
  isLoading: boolean;
  stripeLoaded: boolean;
  isUserLoggedIn: boolean;
  isFormComplete?: boolean;
  onSetCouponId?: (id: string | null) => void;
  reservationExpiry?: Date | null;
  hidePayButton?: boolean;
}

interface CouponInfo {
  valid: boolean;
  id: string;         // Stripe coupon ID
  code: string;       // Promotion code string
  name?: string;
  percent_off?: number;
  amount_off?: number;
  currency?: string;
  error?: string;
}

const PaymentSummary = ({
  price = 0,
  currency = "USD",
  discount,
  discountCode,
  onDiscountCodeChange,
  onApplyDiscount,
  onPaymentSubmit,
  isLoading,
  stripeLoaded,
  isUserLoggedIn,
  isFormComplete = false,
  onSetCouponId,
  reservationExpiry,
  hidePayButton = false
}: PaymentSummaryProps) => {
  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);
  const [couponInfo, setCouponInfo] = useState<CouponInfo | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [showStripeError, setShowStripeError] = useState(true);
  const [showLoginError, setShowLoginError] = useState(true);
  const [showFormError, setShowFormError] = useState(true);
  const [showValidationError, setShowValidationError] = useState(true);
  const [timeRemaining, setTimeRemaining] = useState<string>("");
  const [isExpired, setIsExpired] = useState(false);

  // Auto-hide error alerts after 8 seconds
  useEffect(() => {
    if (validationError && showValidationError) {
      const timer = setTimeout(() => {
        setShowValidationError(false);
      }, 8000);
      return () => clearTimeout(timer);
    }
  }, [validationError, showValidationError]);

  useEffect(() => {
    if (!stripeLoaded && showStripeError) {
      const timer = setTimeout(() => {
        setShowStripeError(false);
      }, 8000);
      return () => clearTimeout(timer);
    }
  }, [stripeLoaded, showStripeError]);

  useEffect(() => {
    if (!isUserLoggedIn && showLoginError) {
      const timer = setTimeout(() => {
        setShowLoginError(false);
      }, 8000);
      return () => clearTimeout(timer);
    }
  }, [isUserLoggedIn, showLoginError]);

  useEffect(() => {
    if (!isFormComplete && showFormError) {
      const timer = setTimeout(() => {
        setShowFormError(false);
      }, 8000);
      return () => clearTimeout(timer);
    }
  }, [isFormComplete, showFormError]);

  // Reset error visibility when errors change
  useEffect(() => {
    setShowValidationError(true);
  }, [validationError]);

  useEffect(() => {
    setShowStripeError(true);
  }, [stripeLoaded]);

  useEffect(() => {
    setShowLoginError(true);
  }, [isUserLoggedIn]);

  useEffect(() => {
    setShowFormError(true);
  }, [isFormComplete]);

  // Countdown timer
  useEffect(() => {
    if (!reservationExpiry) {
      setTimeRemaining("");
      setIsExpired(false);
      return;
    }

    const updateTimer = () => {
      const now = new Date().getTime();
      const expiry = new Date(reservationExpiry).getTime();
      const distance = expiry - now;

      if (distance <= 0) {
        setTimeRemaining("0:00");
        setIsExpired(true);
        return;
      }

      const minutes = Math.floor(distance / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);
      setTimeRemaining(`${minutes}:${seconds.toString().padStart(2, '0')}`);
      setIsExpired(false);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [reservationExpiry]);

  // Auto-validate discount code when it changes
  useEffect(() => {
    // Only auto-validate if:
    // 1. There's a discount code
    // 2. It's not already being validated
    // 3. We don't already have valid coupon info for this code
    if (discountCode.trim() && !isValidatingCoupon && couponInfo?.code !== discountCode.trim()) {
      // Add debounce delay to avoid validating on every keystroke
      const timeoutId = setTimeout(() => {
        console.log('Auto-validating discount code:', discountCode.trim());
        validateCoupon();
      }, 500); // Wait 500ms after user stops typing

      return () => clearTimeout(timeoutId);
    }

    // Clear coupon info if discount code is removed
    if (!discountCode.trim() && couponInfo) {
      setCouponInfo(null);
      setValidationError(null);
      if (onSetCouponId) {
        onSetCouponId(null);
      }
    }
  }, [discountCode]);

  // Calculate discounts
  let discountAmount = 0;
  let finalPrice = price;

  // Apply percentage discount if provided
  if (discount > 0) {
    discountAmount = (price * discount) / 100;
    finalPrice = price - discountAmount;
  }

  // Apply coupon amount discount if available
  if (couponInfo?.valid && couponInfo.amount_off) {
    const couponDiscount = couponInfo.amount_off / 100; // Convert from cents
    finalPrice = Math.max(0, finalPrice - couponDiscount);
  }

  // Apply coupon percentage discount if available
  if (couponInfo?.valid && couponInfo.percent_off) {
    const couponDiscount = (price * couponInfo.percent_off) / 100;
    finalPrice = Math.max(0, price - couponDiscount);
  }

  const formatPrice = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormComplete) {
      return;
    }
    onPaymentSubmit();
  };

  const validateCoupon = async () => {
    if (!discountCode.trim()) return;
    
    setIsValidatingCoupon(true);
    setValidationError(null);
    setCouponInfo(null);
    
    try {
      console.log("Validating coupon code:", discountCode.trim());
      
      const { data, error } = await supabase.functions.invoke('validate-coupon', {
        body: { couponCode: discountCode.trim() }
      });
      
      if (error) {
        console.error("Edge function error:", error);
        setValidationError(error.message || 'Failed to validate coupon');
        return;
      }
      
      console.log("Coupon validation response:", data);
      
      if (!data) {
        setValidationError('Invalid response from server');
        return;
      }
      
      setCouponInfo(data);
      
      if (data.valid) {
        // Update the displayed discount code to the actual code string from the response
        onDiscountCodeChange(data.code);
        
        // CRITICAL FIX: Send the promotion code (data.code) instead of coupon ID (data.id)
        // The edge function expects the promotion code, not the coupon ID
        if (onSetCouponId) {
          onSetCouponId(data.code); // Changed from data.id to data.code
        }
        
        // If we have a valid coupon, apply it - handle both percent_off and amount_off
        if (data.percent_off || data.amount_off) {
          console.log("Valid coupon with discount:", { 
            percent_off: data.percent_off, 
            amount_off: data.amount_off 
          });
          onApplyDiscount();
        } else {
          console.log("Valid coupon but no discount information found");
        }
      } else {
        setValidationError(data.error || 'Invalid coupon code');
        if (onSetCouponId) {
          onSetCouponId(null);
        }
      }
    } catch (err: any) {
      console.error("Exception in coupon validation:", err);
      setValidationError(err.message || 'An error occurred while validating the coupon');
      if (onSetCouponId) {
        onSetCouponId(null);
      }
    } finally {
      setIsValidatingCoupon(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Payment Summary</span>
          {reservationExpiry && (
            <Badge variant={isExpired ? "destructive" : "secondary"} className="text-sm font-mono">
              {isExpired ? "Expired" : timeRemaining}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit}>
          {reservationExpiry && !isExpired && (
            <Alert className="mb-4">
              <AlertDescription className="text-sm">
                This slot is reserved for you. Complete payment within <strong>{timeRemaining}</strong> to secure your booking.
              </AlertDescription>
            </Alert>
          )}
          
          {isExpired && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Your reservation has expired. Please select a new time slot.
              </AlertDescription>
            </Alert>
          )}
          
          <div className="space-y-4">
            <div className="flex justify-between">
              <span>Service Price:</span>
              <span>{formatPrice(price)}</span>
            </div>
            
            {discount > 0 && (
              <div className="flex justify-between text-green-600">
                <span>Discount ({discount}%):</span>
                <span>-{formatPrice(discountAmount)}</span>
              </div>
            )}
            
            {couponInfo?.valid && couponInfo.amount_off && (
              <div className="flex justify-between text-green-600">
                <span>Coupon Discount:</span>
                <span>-{formatPrice(couponInfo.amount_off/100)}</span>
              </div>
            )}

            {couponInfo?.valid && couponInfo.percent_off && (
              <div className="flex justify-between text-green-600">
                <span>Coupon Discount ({couponInfo.percent_off}%):</span>
                <span>-{formatPrice((price * couponInfo.percent_off) / 100)}</span>
              </div>
            )}
            
            <Separator />
            
            <div className="flex justify-between font-medium">
              <span>Total:</span>
              <span>{formatPrice(finalPrice)}</span>
            </div>

            {price > 0 && (
              <div>
                <div className="text-sm font-medium mb-1.5">Discount Code:</div>
                <div className="flex space-x-2">
                  <Input
                    value={discountCode}
                    onChange={(e) => onDiscountCodeChange(e.target.value)}
                    placeholder="Enter code"
                    className="flex-1"
                    disabled={isValidatingCoupon}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={validateCoupon}
                    disabled={!discountCode.trim() || isValidatingCoupon}
                  >
                    {isValidatingCoupon ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Validating...
                      </>
                    ) : (
                      'Verify'
                    )}
                  </Button>
                </div>
              </div>
            )}

            {couponInfo && couponInfo.valid && (
              <Alert variant="default" className="bg-green-50 border-green-200">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription className="flex justify-between items-center">
                  <span>
                    {couponInfo.name || couponInfo.code} applied
                    {couponInfo.percent_off && ` (${couponInfo.percent_off}% off)`}
                    {couponInfo.amount_off && ` (${formatPrice(couponInfo.amount_off/100)} off)`}
                  </span>
                  <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200">
                    Valid
                  </Badge>
                </AlertDescription>
              </Alert>
            )}

            {validationError && showValidationError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{validationError}</AlertDescription>
              </Alert>
            )}

            {!stripeLoaded && showStripeError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Payment system is not available. Please try again later.
                </AlertDescription>
              </Alert>
            )}
            
            {!isUserLoggedIn && showLoginError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  You need to be logged in to complete your booking.
                </AlertDescription>
              </Alert>
            )}
            
            {!isFormComplete && showFormError && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Please select a date and time to proceed with payment.
                </AlertDescription>
              </Alert>
            )}
          </div>
        </form>
      </CardContent>
      {!hidePayButton && (
        <CardFooter>
          <Button 
            onClick={onPaymentSubmit}
            disabled={isLoading || !stripeLoaded || !isUserLoggedIn || !isFormComplete || isExpired} 
            className="w-full"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              "Proceed to Payment"
            )}
          </Button>
        </CardFooter>
      )}
    </Card>
  );
};

export default PaymentSummary;
